// Section 4 phase 5 and Section 17: rationing. Per resource (food, water, warmth):
//   1-2. demand (consumption phase) against stock; covered stock feeds everyone in full;
//   3.   otherwise the resource's ration policy allocates shares (allocate.ts, groups.ts);
//   4.   the player's standing pins are honoured before the policy fills the rest;
//   5.   if someone unpinned falls below the floor (or the policy is Manual) and askOnShortage is
//        on, the season pauses on a ShortagePrompt; the answer's shares, seed and saved order
//        are applied on resume (or up front, in replay). Projection never pauses;
//   6.   shares are recorded as need satisfaction, memories, the report and the Ledger.
// Then household sharing (food), the Section 16 rationing trust rows, and the need streaks.
// Planning is pure (plan.ts); nothing is written to the draft until every resource is planned,
// so a pause leaves the draft untouched (24.4, ADR-0007).
import { peopleSorted } from '../../core/queries';
import type {
  NeedSatisfaction,
  Person,
  PersonId,
  Phase,
  PhaseContext,
  SavedPriorityOrder,
} from '../../core/types';
import { bedsGini, foodGini } from '../shared/metrics';
import { seal } from '../shared/seal';
import { addStock } from '../shared/stocks';
import { EPS, planResource, RATIONED, type Rationed, type ResourcePlan } from './plan';
import { orderFromShares, recordLedger, recordMemories, shortGroups } from './record';
import { shareWithinHouseholds } from './sharing';
import { updateStreaks } from './streaks';
import { applyRationingTrust } from './trust';

type Shares = Record<PersonId, number>;

/** Final shares for everyone (1 for people with no demand). */
function complete(plan: ResourcePlan, people: readonly Person[]): Shares {
  const out: Shares = {};
  for (const p of people) {
    const d = plan.demand[p.id] ?? 0;
    out[p.id] = d > 0 ? Math.min(1, Math.max(0, plan.final[p.id] ?? 1)) : 1;
  }
  return out;
}

/** Move stock: eaten seed into food, then the ration out of stock (warmth: wood, then cloth). */
function distribute(
  ctx: PhaseContext,
  plan: ResourcePlan,
  people: readonly Person[],
  given: Shares,
): number {
  if (plan.seedEaten > EPS) {
    ctx.draft.seedReserved = Math.max(0, ctx.draft.seedReserved - plan.seedEaten);
    addStock(ctx, 'food', plan.seedEaten, 'other');
    ctx.report.add({ kind: 'seed', action: 'eaten', amount: plan.seedEaten });
  }
  let total = 0;
  for (const p of people) total += (given[p.id] ?? 1) * (plan.demand[p.id] ?? 0);
  const distributed = Math.min(total, plan.available);
  switch (plan.resource) {
    case 'food':
      addStock(ctx, 'food', -distributed, 'consumption');
      break;
    case 'water':
      addStock(ctx, 'water', -distributed, 'consumption');
      break;
    case 'warmth': {
      const wood = -addStock(ctx, 'wood', -distributed, 'consumption');
      const rest = distributed - wood;
      if (rest > EPS) addStock(ctx, 'cloth', -rest / ctx.cfg.needs.clothWarmth, 'consumption');
      break;
    }
  }
  ctx.report.current.distribution[plan.resource] = { available: plan.available, distributed };
  return distributed;
}

/** Report the outcome, save the order and the dialog's pins (Section 17, 16 norms, 21). */
function reportOutcome(
  ctx: PhaseContext,
  plan: ResourcePlan,
  people: readonly Person[],
  given: Shares,
  shared: number,
): void {
  const short = new Set(
    people
      .filter(
        (p) => (plan.demand[p.id] ?? 0) > 0 && (given[p.id] ?? 1) < ctx.cfg.needs.shortThreshold,
      )
      .map((p) => p.id),
  );
  if (plan.shortage) {
    ctx.report.add({
      kind: 'shortage',
      resource: plan.resource,
      covered: people.filter((p) => (given[p.id] ?? 1) >= 1 - EPS).length,
      population: people.length,
      needed: plan.needed,
      available: plan.available,
      shortfall: Math.max(0, plan.needed - plan.available),
      policy: plan.policy,
    });
    ctx.draft.counters.lastShortageTurn = ctx.season.turn;
  }
  if (plan.shortage || short.size > 0) {
    ctx.report.add({
      kind: 'rationing-outcome',
      resource: plan.resource,
      policy: plan.policy,
      groupsOrder: [...plan.order],
      shortGroups: shortGroups(ctx, plan, short),
      byHand: plan.byHand,
      pinnedNone: plan.pinnedNone,
      announced: ctx.state.policies.announceRations,
      deviated: plan.deviated,
      shared,
    });
  }
  if (plan.saveAsOrder !== null) {
    const saved = ctx.draft.savedPriorityOrders;
    let id = `saved-t${ctx.season.turn}-${plan.resource}`;
    for (let n = 2; saved.some((o) => o.id === id); n++)
      id = `saved-t${ctx.season.turn}-${plan.resource}-${n}`;
    const order: SavedPriorityOrder = {
      id,
      name: plan.saveAsOrder,
      groups: orderFromShares(ctx, plan),
      announced: ctx.state.policies.announceRations,
    };
    saved.push(order);
  }
  const pins = ctx.draft.rationOverrides[plan.resource];
  for (const id of Object.keys(plan.answerPins).sort() as PersonId[]) {
    const share = plan.answerPins[id];
    const p = ctx.state.people[id];
    if (share === undefined || p === undefined) continue;
    if (plan.resource === 'warmth') pins.households[p.householdId] = share;
    else pins.people[id] = share;
  }
}

export const rationingPhase: Phase = {
  id: 'rationing',
  reads: [
    'people',
    'households',
    'stocks',
    'seedReserved',
    'policies',
    'rationOverrides',
    'myHousehold',
    'promptAnswers',
    'currentReport',
  ],
  writes: [
    'stocks',
    'seedReserved',
    'people',
    'counters',
    'ledger',
    'currentReport',
    'savedPriorityOrders',
    'rationOverrides',
  ],
  projection: 'run',
  run(ctx) {
    const committed = ctx.state.currentReport;
    if (committed === null) return 'done';
    const people = peopleSorted(ctx.state);
    const plans: ResourcePlan[] = [];
    for (const r of RATIONED) {
      const plan = planResource(ctx, r, people, committed.demand[r] ?? {});
      // Nothing has been written yet, so pausing here leaves the draft untouched (24.4).
      if (plan.prompt !== null) return { paused: plan.prompt };
      plans.push(plan);
    }

    const given = {} as Record<Rationed, Shares>;
    const received = {} as Record<Rationed, Shares>;
    const shared = { food: 0, water: 0, warmth: 0 } as Record<Rationed, number>;
    for (const plan of plans) {
      given[plan.resource] = complete(plan, people);
      received[plan.resource] = given[plan.resource];
    }
    if (ctx.state.policies.householdSharing) {
      const food = plans.find((p) => p.resource === 'food' && !p.full);
      if (food !== undefined) {
        const result = shareWithinHouseholds(
          people,
          food.demand,
          given.food,
          ctx.cfg.rationing.householdSharingFraction,
          ctx.cfg.needs.shortThreshold,
        );
        received.food = result.shares;
        shared.food = result.shared;
      }
    }
    // Resources someone was rationed on (the rest fed everyone in full: nothing to record).
    const rationed = plans.filter((p) => !p.full);
    for (const plan of plans) distribute(ctx, plan, people, given[plan.resource]);
    for (const plan of rationed)
      reportOutcome(ctx, plan, people, given[plan.resource], shared[plan.resource]);

    // One write per person: this season's needs with food, water and warmth resolved.
    const needs: Record<PersonId, NeedSatisfaction> = {};
    for (const p of people) {
      const base = committed.needs[p.id];
      if (base === undefined) continue;
      needs[p.id] = {
        ...base,
        food: received.food[p.id] ?? 1,
        water: received.water[p.id] ?? 1,
        warmth: received.warmth[p.id] ?? 1,
      };
    }
    const report = ctx.report.current;
    report.needs = seal(ctx, needs);
    // Allotments are kept only for resources someone was rationed on (saves stay small).
    const rations: NonNullable<typeof report.rations> = {};
    for (const plan of rationed) {
      const shares = given[plan.resource];
      if (people.some((p) => (shares[p.id] ?? 1) < 1 - EPS)) rations[plan.resource] = shares;
    }
    if (Object.keys(rations).length > 0) report.rations = seal(ctx, rations);
    report.gini = { food: foodGini(report), beds: bedsGini(ctx.state, report) };

    if (rationed.length > 0) {
      applyRationingTrust(ctx, people, rationed);
      recordMemories(ctx, people, rationed, received);
    }
    if (ctx.mode === 'live') recordLedger(ctx, people, rationed, needs);
    // Streaks follow the needs just resolved, so later phases' modifiers see them (ADR-0010).
    for (const p of people) {
      const n = needs[p.id];
      if (n !== undefined) updateStreaks(ctx, p, n);
    }
    return 'done';
  },
};
